import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, Phone, Check, Globe } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { COVERAGE_AREAS, SERVICES, BUSINESS, FAQS } from '../data/content';
import QuoteForm from '../components/QuoteForm';
import TrustBar from '../components/TrustBar';
import SEO from '../components/SEO';
import { Reveal } from '../hooks/useReveal';

export default function CoveragePage() {
  const { slug } = useParams();
  const area = COVERAGE_AREAS.find(c => c.slug === slug);
  if (!area) return <Navigate to="/coverage" replace />;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: area.title,
    provider: { '@type': 'MovingCompany', name: 'Laraé Logistics', telephone: BUSINESS.phone },
    areaServed: area.title,
    description: area.blurb,
  };

  return (
    <>
      <SEO title={`${area.title} — Removals & Man and Van | Laraé Logistics`}
        description={`${area.short} Fully insured, family-run removals across ${area.title.toLowerCase()}. Call ${BUSINESS.phone} for a fixed-price quote today.`}
        jsonLd={jsonLd} />

      <section className="bg-forest text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <p className="font-manrope text-sm text-oat uppercase tracking-widest mb-3">Coverage</p>
            <h1 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight">{area.title}</h1>
            <p className="font-manrope text-base md:text-lg text-white/80 mt-5 max-w-xl leading-relaxed">{area.blurb}</p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link to="/quote" className="bg-oat text-ink font-semibold rounded-full px-7 py-4 inline-flex items-center justify-center gap-2 hover:bg-oat/90 transition-all hover:translate-x-0.5">Get Free Quote <ArrowRight size={18} /></Link>
              <a href={`tel:${BUSINESS.phoneRaw}`} className="bg-white/10 border border-white/30 text-white font-medium rounded-full px-7 py-4 inline-flex items-center justify-center gap-2 hover:bg-white/20 transition-colors"><Phone size={18} /> {BUSINESS.phone}</a>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-7 backdrop-blur">
              <h3 className="font-outfit text-lg font-semibold text-white mb-4">What's included</h3>
              <ul className="space-y-3">
                {area.bullets.map(b => (
                  <li key={b} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-oat grid place-items-center shrink-0 mt-0.5"><Check size={12} className="text-forest" /></div>
                    <span className="font-manrope text-sm text-white/90">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <TrustBar />

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <Reveal>
            <h2 className="font-outfit text-2xl md:text-3xl font-medium text-ink mb-8">Services we offer</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICES.slice(0, 8).map((s, i) => (
              <Reveal key={s.slug} delay={i * 60}>
                <Link to={`/services/${s.slug}`} className="block bg-white border border-sand rounded-2xl p-5 hover:border-forest hover:shadow-md hover:-translate-y-0.5 transition-all">
                  <div className="font-outfit text-base font-semibold text-ink">{s.title}</div>
                  <div className="font-manrope text-xs text-ink/60 mt-1">{s.short}</div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sand/50 py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-5 md:px-8 lg:px-12">
          <Reveal>
            <div className="text-center max-w-xl mx-auto mb-8">
              <h2 className="font-outfit text-3xl md:text-4xl font-medium text-ink tracking-tight">Get your fixed-price quote</h2>
              <p className="font-manrope text-base text-ink/65 mt-3">Fully insured. No deposit. Reply within 10 minutes during working hours.</p>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <QuoteForm sourcePage={`coverage-${slug}`} />
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-5 md:px-8 lg:px-12">
          <Reveal>
            <h2 className="font-outfit text-2xl md:text-3xl font-medium text-ink mb-6 text-center">Frequently asked questions</h2>
          </Reveal>
          <Accordion type="single" collapsible>
            {FAQS.slice(0, 5).map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-sand">
                <AccordionTrigger className="font-outfit text-base font-medium text-ink text-left">{f.q}</AccordionTrigger>
                <AccordionContent className="font-manrope text-sm text-ink/70 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  );
}
